import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Offcanvas,
} from "reactstrap";
import Select from "react-select";
import {
  getStandardTypes,
  getWorkTypes,
  getIndustryTypes,
  getReviewTypes,
} from "../../helpers/backend_helper";
import { postData } from "../../helpers/url-request";
import {
  newQuestion,
  readChecklistDocument,
  questionDescription,
  matchChecklistStandard,
} from "./checklistImport";
import "./ChecklistImportWizard.scss";
import ChecklistQuestionEditor from "./ChecklistQuestionEditor";
import ChecklistImportPreview from "./ChecklistImportPreview";
import ChecklistImportSuccess from "./ChecklistImportSuccess";
import FullScreenPageLayout from "../../components/Common/FullScreenPageLayout";

const initialDraft = () => ({
  name: "",
  code: "",
  edition: "",
  standardTypeId: "",
  workTypes: [],
  industryTypes: [],
  reviewTypes: [],
  questions: [],
  sourceName: "",
  sourceText: "",
  warnings: [],
});

export default function ChecklistImportWizard() {
  const navigate = useNavigate();
  const [pendingFile, setPendingFile] = useState(null);
  const [draft, setDraft] = useState(initialDraft);
  const [step, setStep] = useState(1);
  const [standards, setStandards] = useState([]);
  const [typeOptions, setTypeOptions] = useState({
    workTypes: [],
    industryTypes: [],
    reviewTypes: [],
  });
  const [section, setSection] = useState("");
  const [sectionSearch, setSectionSearch] = useState("");
  const [search, setSearch] = useState("");
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [selected, setSelected] = useState([]);
  const [editing, setEditing] = useState(null);
  const [sourceOpen, setSourceOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkType, setBulkType] = useState("None");
  const [busy, setBusy] = useState(false);
  const [confirmCreation, setConfirmCreation] = useState(false);
  const [createdChecklist, setCreatedChecklist] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const fileInput = useRef();
  const submitting = useRef(false);

  const update = (changes) =>
    setDraft((current) => ({ ...current, ...changes }));

  const patchQuestion = (key, changes) =>
    setDraft((current) => ({
      ...current,
      questions: current.questions.map((q) =>
        q.key === key ? { ...q, ...changes } : q
      ),
    }));
  useEffect(() => {
    Promise.all([
      getStandardTypes(),
      getWorkTypes(),
      getIndustryTypes(),
      getReviewTypes(),
    ])
      .then(
        ([
          standardsResponse,
          workResponse,
          industryResponse,
          reviewResponse,
        ]) => {
          if (
            ![
              standardsResponse,
              workResponse,
              industryResponse,
              reviewResponse,
            ].every((response) => response?.status)
          )
            throw new Error("Unable to load checklist options.");
          setStandards(standardsResponse.data || []);

          const options = (response) =>
            (response.data || []).map((item) => ({
              value: item.id,
              label: item.name,
            }));
          const reviewTypes = options(reviewResponse);
          setTypeOptions({
            workTypes: options(workResponse),
            industryTypes: options(industryResponse),
            reviewTypes,
          });
          if (reviewTypes.length === 1) {
            setDraft((current) =>
              current.reviewTypes.length
                ? current
                : { ...current, reviewTypes: [reviewTypes[0].value] }
            );
          }
        }
      )
      .catch(() =>
        setError(
          "Unable to load checklist options. Reload this page to try again."
        )
      );
  }, []);
  const sections = useMemo(
    () => [...new Set(draft.questions.map((q) => q.section))],
    [draft.questions]
  );
  const currentSection = sections.includes(section) ? section : sections[0];
  // Wording warnings are advisory; required question fields determine validity.
  const flagged = draft.questions.filter((q) => q.needsCheck).length;
  const filtered = draft.questions.filter(
    (q) =>
      q.section === currentSection &&
      (!onlyFlagged || q.needsCheck) &&
      `${q.question} ${q.referenceNo}`
        .toLowerCase()
        .includes(search.toLowerCase())
  );
  const recordsPerPage =
    pageSize === "all" ? Math.max(1, filtered.length) : pageSize;
  const pages = Math.max(1, Math.ceil(filtered.length / recordsPerPage));
  const currentPage = Math.min(page, pages);
  const visible = filtered.slice(
    (currentPage - 1) * recordsPerPage,
    currentPage * recordsPerPage
  );
  const valid =
    draft.name.trim() &&
    draft.standardTypeId &&
    draft.questions.length &&
    draft.questions.every(
      (q) => q.question.trim() && q.section.trim() && q.referenceNo.trim()
    );

  const loadFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const document = await readChecklistDocument(file);
      const questions = document.questions.map((question) => ({
        ...question,
        answerType: "None",
        required: false,
      }));
      update({
        questions,
        standardTypeId:
          draft.standardTypeId || matchChecklistStandard(document, standards),
        sourceName: file.name,
        sourceText:
          document.sourceText ||
          questions
            .map((q) => `[${q.section}]\n${q.referenceNo}: ${q.question}`)
            .join("\n\n"),
        name: draft.name || document.name || file.name.replace(/\.[^.]+$/, ""),
        code: draft.code || document.code || "",
        edition: draft.edition || document.edition || "",
        warnings: document.warnings || [],
      });
      setSelected([]);
      setSection("");
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const saveDraft = () => {
    const blob = new Blob(
      [
        JSON.stringify(
          { format: "piers-checklist-draft-v1", draft, step },
          null,
          2
        ),
      ],
      { type: "application/json" }
    );
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "checklist-draft.json";
    anchor.click();
    URL.revokeObjectURL(url);
    setNotice("Draft downloaded.");
  };

  const create = async () => {
    if (submitting.current || !valid) return;
    submitting.current = true;
    setConfirmCreation(false);
    setStep(5);
    setBusy(true);
    setError("");
    try {
      const response = await postData("/checklist/import", {
        name: draft.name.trim(),
        standardTypeId: Number(draft.standardTypeId),
        description: [
          draft.code && `Code: ${draft.code}`,
          draft.edition && `Edition: ${draft.edition}`,
          `Imported from ${draft.sourceName || "pasted text"}`,
        ]
          .filter(Boolean)
          .join("\n"),
        clientTypes: [],
        industryTypes: draft.industryTypes,
        workTypes: draft.workTypes,
        reviewTypes: draft.reviewTypes,
        questions: draft.questions.map((q, i) => ({
          section: q.section.trim(),
          topic:
            q.topic ||
            q.instructions?.match(/^Topic:\s*(.+)$/m)?.[1]?.trim() ||
            q.section.trim(),
          question: q.question.trim(),
          referenceNo: q.referenceNo.trim(),
          description: questionDescription(q),
          sequenceId: i + 1,
        })),
      });
      if (!response.status)
        throw new Error(response.message || "Unable to create checklist.");
      setCreatedChecklist({
        id: response.data.id,
        name: draft.name.trim(),
        sections: sections.length,
        questions: draft.questions.length,
      });
    } catch (err) {
      setError(err.message || "Unable to create checklist.");
    } finally {
      setBusy(false);
      submitting.current = false;
    }
  };

  const addQuestion = () =>
    setEditing({
      question: {
        ...newQuestion("", currentSection || "General", draft.questions.length),
        key: `new-${Date.now()}`,
      },
      isNew: true,
    });

  const editQuestion = (question) =>
    setEditing({ question: { ...question }, isNew: false });

  const saveQuestion = (question) => {
    if (editing.isNew) {
      update({ questions: [...draft.questions, question] });
      setSection(question.section);
      setSearch("");
      setOnlyFlagged(false);
      setPage(
        Math.ceil(
          (draft.questions.filter((q) => q.section === question.section)
            .length +
            1) /
            (pageSize === "all"
              ? Math.max(1, draft.questions.length + 1)
              : pageSize)
        )
      );
    } else {
      patchQuestion(question.key, question);
      setSection(question.section);
    }
    setEditing(null);
  };

  const selectQuestion = (key) =>
    setSelected((current) =>
      current.includes(key)
        ? current.filter((id) => id !== key)
        : [...current, key]
    );

  return (
    <FullScreenPageLayout
      className="checklist-import"
      breadcrumbs={[
        { label: "Administration", to: "/firm/dashboard" },
        { label: "Checklists", to: "/system/checklist" },
        { label: "Import checklist" },
      ]}
      actions={
        <>
          <button
            type="button"
            onClick={() => navigate("/system/checklist")}
            disabled={busy}
          >
            <i className="bx bx-x" /> Close
          </button>
          {!createdChecklist && (
            <button type="button" onClick={saveDraft} disabled={busy}>
              <i className="bx bx-save" /> Save draft
            </button>
          )}
        </>
      }
      steps={
        <ol className="checklist-import__steps">
          {[
            "Upload document",
            "Checklist basic details",
            "Check questions",
            "Preview checklist",
            "Create checklist",
          ].map((label, index) => (
            <li
              key={label}
              className={
                createdChecklist
                  ? "complete"
                  : step === index + 1
                  ? "active"
                  : step > index + 1
                  ? "complete"
                  : ""
              }
            >
              <button
                aria-current={step === index + 1 ? "step" : undefined}
                disabled={
                  busy ||
                  step === 5 ||
                  index === 4 ||
                  (index > 0 && !draft.questions.length) ||
                  (index >= 2 &&
                    (!draft.name.trim() || !draft.standardTypeId)) ||
                  (index >= 3 && !valid)
                }
                onClick={() => {
                  setStep(index + 1);
                  setError("");
                }}
              >
                <span>
                  {createdChecklist || step > index + 1 ? "✓" : index + 1}
                </span>
                {label}
              </button>
            </li>
          ))}
        </ol>
      }
    >
      <div className="checklist-import__shell" aria-busy={busy}>
        {error && (
          <div className="checklist-import__error" role="alert">
            {error}
          </div>
        )}
        {notice && (
          <div className="checklist-import__notice" role="status">
            {notice}
          </div>
        )}
        {step === 1 ? (
          <section className="checklist-import__upload">
            <div className="checklist-import__upload-card">
              <h2>Upload your checklist document</h2>
              <p>Organise questions into sections for you to check.</p>
              <div
                className="checklist-import__drop"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (!busy) setPendingFile(e.dataTransfer.files[0] || null);
                }}
              >
                <i className="bx bx-cloud-upload" />
                <h3>Drag and drop your file here</h3>
                <p>or</p>
                <button
                  className="primary"
                  disabled={busy}
                  onClick={() => fileInput.current.click()}
                >
                  {busy ? "Reading document…" : "Browse files"}
                </button>
                <input
                  hidden
                  type="file"
                  ref={fileInput}
                  accept=".pdf,.xlsx,.xls,.csv,.txt,.md"
                  onChange={(e) => setPendingFile(e.target.files[0] || null)}
                />
                <p>PDF or Excel · Up to 10 MB · 1,000 questions</p>
              </div>
              {pendingFile && (
                <div className="checklist-import__file">
                  <i className="bx bx-file" />
                  <div>
                    <strong>{pendingFile.name}</strong>
                    <small>Ready to extract</small>
                  </div>
                  <button
                    disabled={busy}
                    aria-label="Remove selected file"
                    onClick={() => {
                      setPendingFile(null);
                      if (fileInput.current) fileInput.current.value = "";
                    }}
                  >
                    <i className="bx bx-x" />
                  </button>
                </div>
              )}
              <div className="checklist-import__upload-actions">
                <button
                  disabled={busy}
                  onClick={() => navigate("/system/checklist")}
                >
                  Cancel
                </button>
                <button
                  className="primary"
                  disabled={busy || !pendingFile}
                  onClick={() => loadFile(pendingFile)}
                >
                  {busy ? "Extracting data..." : "Extract Data"}{" "}
                  <i className="bx bx-right-arrow-alt" />
                </button>
              </div>
            </div>
            <aside className="checklist-import__upload-help">
              <h2>What will be extracted</h2>
              {[
                [
                  "bx-list-ul",
                  "Sections and question numbers",
                  "Organise questions into logical sections.",
                ],
                [
                  "bx-message-rounded",
                  "Questions and answer options",
                  "Extract questions and available answer options.",
                ],
                [
                  "bx-file",
                  "Required information fields",
                  "Identify additional information required for each question.",
                ],
                [
                  "bx-link",
                  "Question instructions and references",
                  "Capture instructions, guidance and references.",
                ],
              ].map(([icon, title, description]) => (
                <div className="checklist-import__feature" key={title}>
                  <i className={`bx ${icon}`} />
                  <div>
                    <h3>{title}</h3>
                    <p>{description}</p>
                  </div>
                </div>
              ))}
              <div className="checklist-import__control">
                <strong>You stay in control</strong>
                <p>
                  Check and edit the extracted content before creating the
                  checklist.
                </p>
              </div>
            </aside>
          </section>
        ) : step === 2 ? (
          <section className="checklist-import__basic-details">
            <h2>Checklist basic details</h2>
            <p>
              Confirm the checklist details before checking the extracted
              questions.
            </p>
            <fieldset disabled={busy} className="checklist-import__fields">
              <label>
                Checklist name
                <input
                  value={draft.name}
                  onChange={(e) => update({ name: e.target.value })}
                  placeholder="Enter checklist name"
                />
              </label>
              <label>
                Code
                <input
                  value={draft.code}
                  onChange={(e) => update({ code: e.target.value })}
                  placeholder="e.g. PRP 20,400"
                />
              </label>
              <label>
                Edition
                <input
                  value={draft.edition}
                  onChange={(e) => update({ edition: e.target.value })}
                  placeholder="e.g. May 2024"
                />
              </label>
              <label>
                Standard <span aria-hidden="true">*</span>
                <select
                  value={draft.standardTypeId}
                  onChange={(e) => update({ standardTypeId: e.target.value })}
                >
                  <option value="">Select standard</option>
                  {standards.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </label>
              {[
                ["workTypes", "Work Types"],
                ["industryTypes", "Industry Types"],
                ["reviewTypes", "Review Types"],
              ].map(([field, label]) => (
                <div key={field}>
                  <label htmlFor={`import-${field}`}>{label}</label>
                  <Select
                    inputId={`import-${field}`}
                    isMulti
                    options={typeOptions[field]}
                    value={typeOptions[field].filter((option) =>
                      draft[field].includes(option.value)
                    )}
                    onChange={(values) =>
                      update({
                        [field]: (values || []).map((option) => option.value),
                      })
                    }
                    placeholder={`Select ${label.toLowerCase()}`}
                  />
                </div>
              ))}
            </fieldset>
          </section>
        ) : (
          <>
            {step === 3 ? (
              <div className="checklist-import__workspace">
                <aside>
                  <strong className="checklist-import__section-totals">
                    {sections.length} sections &middot; {draft.questions.length}{" "}
                    questions
                  </strong>
                  <button
                    type="button"
                    className="checklist-import__view-source"
                    onClick={() => setSourceOpen(true)}
                  >
                    <i className="bx bx-file" /> View source
                  </button>
                  <button
                    className={`checklist-import__needs-checking warning${
                      onlyFlagged ? " active" : ""
                    }`}
                    aria-pressed={onlyFlagged}
                    onClick={() => {
                      setOnlyFlagged(!onlyFlagged);
                      setPage(1);
                    }}
                  >
                    ⚠ Needs checking ({flagged})
                  </button>
                  <input
                    aria-label="Search sections"
                    placeholder="Search sections…"
                    value={sectionSearch}
                    onChange={(e) => setSectionSearch(e.target.value)}
                  />
                  {sections
                    .filter((s) =>
                      s.toLowerCase().includes(sectionSearch.toLowerCase())
                    )
                    .map((s) => (
                      <button
                        key={s}
                        className={s === currentSection ? "selected" : ""}
                        onClick={() => {
                          setSection(s);
                          setPage(1);
                          setSelected([]);
                        }}
                      >
                        <strong>{s}</strong>
                        <small>
                          {
                            draft.questions.filter((q) => q.section === s)
                              .length
                          }{" "}
                          questions
                        </small>
                        <i className="bx bx-chevron-right" />
                      </button>
                    ))}
                </aside>
                <section className="checklist-import__questions">
                  <div className="checklist-import__toolbar">
                    <div>
                      <h2>{currentSection}</h2>
                      <small>
                        {
                          draft.questions.filter(
                            (q) => q.section === currentSection
                          ).length
                        }{" "}
                        questions
                      </small>
                    </div>
                    <input
                      aria-label="Search questions"
                      placeholder="Search questions…"
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                    />
                    <button
                      disabled={busy || !selected.length}
                      onClick={() => setBulkOpen(true)}
                    >
                      <i className="bx bx-edit-alt" /> Bulk edit
                      {selected.length ? ` (${selected.length})` : ""}
                    </button>
                    <button
                      className="primary"
                      disabled={busy}
                      onClick={addQuestion}
                    >
                      + Add question
                    </button>
                  </div>
                  <div className="checklist-import__table-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>
                            <input
                              type="checkbox"
                              aria-label="Select all visible questions"
                              checked={
                                visible.length > 0 &&
                                visible.every((q) => selected.includes(q.key))
                              }
                              onChange={(e) =>
                                setSelected((current) =>
                                  e.target.checked
                                    ? [
                                        ...new Set([
                                          ...current,
                                          ...visible.map((q) => q.key),
                                        ]),
                                      ]
                                    : current.filter(
                                        (key) =>
                                          !visible.some((q) => q.key === key)
                                      )
                                )
                              }
                            />
                          </th>
                          <th>Question ID</th>
                          <th>Question / requirement</th>
                          <th>Answer type</th>
                          <th>Status</th>
                          <th>Edit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visible.map((q) => (
                          <React.Fragment key={q.key}>
                            <tr
                              className={`${
                                editing?.question.key === q.key
                                  ? "expanded"
                                  : ""
                              } ${q.needsCheck ? "needs-check" : ""}`}
                            >
                              <td>
                                <input
                                  type="checkbox"
                                  aria-label={`Select ${q.referenceNo}`}
                                  checked={selected.includes(q.key)}
                                  onChange={() => selectQuestion(q.key)}
                                />
                              </td>
                              <td>
                                {q.referenceNo}
                                {q.sourcePage && (
                                  <small className="d-block text-muted">
                                    PDF p. {q.sourcePage}
                                    {q.sourceEndPage > q.sourcePage
                                      ? `–${q.sourceEndPage}`
                                      : ""}
                                  </small>
                                )}
                              </td>
                              <td>
                                <button
                                  className="checklist-import__question-text"
                                  onClick={() => editQuestion(q)}
                                >
                                  {q.question || "Enter question wording"}
                                </button>
                              </td>
                              <td>{q.answerType}</td>
                              <td>
                                {q.needsCheck ? (
                                  <span className="checklist-import__flag">
                                    ⚠ Check wording
                                  </span>
                                ) : (
                                  <span className="checklist-import__checked">
                                    <i className="bx bx-check-circle" /> Checked
                                  </span>
                                )}
                              </td>
                              <td>
                                <button
                                  aria-label={`Edit ${q.referenceNo}`}
                                  aria-expanded={
                                    editing?.question.key === q.key
                                  }
                                  onClick={() => editQuestion(q)}
                                >
                                  <i className="bx bx-pencil" />
                                </button>
                              </td>
                            </tr>
                          </React.Fragment>
                        ))}
                        {!visible.length && (
                          <tr>
                            <td colSpan={7}>
                              No questions match your filters.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="checklist-import__pagination">
                    <span>
                      {filtered.length
                        ? `Showing ${
                            (currentPage - 1) * recordsPerPage + 1
                          }–${Math.min(
                            currentPage * recordsPerPage,
                            filtered.length
                          )} of ${filtered.length} questions`
                        : "0 questions"}
                    </span>
                    <label className="checklist-import__page-size">
                      Records per page
                      <select
                        aria-label="Records per page"
                        value={pageSize}
                        onChange={(event) => {
                          setPageSize(
                            event.target.value === "all"
                              ? "all"
                              : Number(event.target.value)
                          );
                          setPage(1);
                        }}
                      >
                        {[10, 25, 50, 100].map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                        <option value="all">All</option>
                      </select>
                    </label>
                    <button
                      aria-label="Previous page"
                      disabled={currentPage === 1}
                      onClick={() => setPage(currentPage - 1)}
                    >
                      ‹
                    </button>
                    <span>
                      Page {currentPage} of {pages}
                    </span>
                    <button
                      aria-label="Next page"
                      disabled={currentPage === pages}
                      onClick={() => setPage(currentPage + 1)}
                    >
                      ›
                    </button>
                  </div>
                </section>
              </div>
            ) : step === 4 ? (
              <ChecklistImportPreview draft={draft} />
            ) : createdChecklist ? (
              <ChecklistImportSuccess
                checklist={createdChecklist}
                sourceName={draft.sourceName}
                onSource={() => setSourceOpen(true)}
                onView={() =>
                  navigate(`/system/checklist/${createdChecklist.id}/preview`)
                }
                onBack={() => navigate("/system/checklist")}
                onImport={() => {
                  setDraft(initialDraft());
                  setCreatedChecklist(null);
                  setPendingFile(null);
                  setSelected([]);
                  setSection("");
                  setSectionSearch("");
                  setSearch("");
                  setOnlyFlagged(false);
                  setPage(1);
                  setError("");
                  setNotice("");
                  setStep(1);
                }}
              />
            ) : (
              <section className="checklist-import__confirm">
                <h2>
                  {createdChecklist
                    ? "Checklist created"
                    : busy
                    ? "Creating your checklist"
                    : "Checklist creation failed"}
                </h2>
                <button
                  type="button"
                  className="checklist-import__view-source"
                  onClick={() => setSourceOpen(true)}
                >
                  <i className="bx bx-file" /> View source
                </button>
                <p>
                  {draft.name} · {sections.length} sections ·{" "}
                  {draft.questions.length} reviewed questions
                </p>

                <div role="status" aria-live="polite">
                  {busy && (
                    <>
                      <progress aria-label="Checklist and questions creation progress" />
                      <p>
                        Creating the checklist and {draft.questions.length}{" "}
                        questions. Please wait…
                      </p>
                    </>
                  )}
                  <p>
                    {createdChecklist ? "✓" : busy ? "⏳" : "!"} Checklist:{" "}
                    {createdChecklist
                      ? "Created"
                      : busy
                      ? "In progress"
                      : "Not confirmed"}
                  </p>
                  <p>
                    {createdChecklist ? "✓" : busy ? "⏳" : "!"} Questions:{" "}
                    {createdChecklist
                      ? `${createdChecklist.questions} created`
                      : busy
                      ? "In progress"
                      : "Not confirmed"}
                  </p>
                  {createdChecklist && (
                    <p>
                      Checklist ID: {createdChecklist.id} ·{" "}
                      {createdChecklist.sections} sections ·{" "}
                      {createdChecklist.questions} questions
                    </p>
                  )}
                </div>
              </section>
            )}
          </>
        )}
        {step > 1 && !createdChecklist && (
          <footer className="checklist-import__footer">
            {step === 3 && (
              <div className="checklist-import__footer-guidance">
                {draft.warnings?.length > 0 && (
                  <p role="status">{draft.warnings.join(" ")}</p>
                )}
                <span>
                  <i className="bx bx-info-circle" /> Original wording
                  preserved. Edit questions individually or in bulk.
                </span>
              </div>
            )}
            <div>
              <button
                disabled={busy || Boolean(createdChecklist)}
                onClick={() =>
                  step === 1 ? navigate("/system/checklist") : setStep(step - 1)
                }
              >
                {step === 1 ? "Cancel" : "Back"}
              </button>
              {step === 2 && (
                <button
                  className="primary"
                  disabled={busy || !draft.name.trim() || !draft.standardTypeId}
                  onClick={() => setStep(3)}
                >
                  Continue to check questions
                </button>
              )}
              {step === 3 && (
                <>
                  <button
                    className="primary"
                    disabled={busy || !valid}
                    onClick={() => setStep(4)}
                  >
                    Continue
                  </button>
                </>
              )}
              {step === 4 && (
                <button
                  className="primary"
                  disabled={busy || !valid}
                  onClick={() => setConfirmCreation(true)}
                >
                  Create Checklist
                </button>
              )}
              {step === 5 && createdChecklist && (
                <button
                  className="primary"
                  disabled={busy || !valid}
                  onClick={() =>
                    navigate(`/system/checklist/${createdChecklist.id}/preview`)
                  }
                >
                  View created checklist
                </button>
              )}
            </div>
          </footer>
        )}
        <Offcanvas
          isOpen={Boolean(editing)}
          direction="end"
          toggle={() => setEditing(null)}
          className="checklist-import checklist-question-drawer"
          aria-label={editing?.isNew ? "Add question" : "Edit question"}
        >
          {editing && (
            <ChecklistQuestionEditor
              key={editing.question.key}
              question={editing.question}
              isNew={editing.isNew}
              sourceName={draft.sourceName}
              onSource={() => setSourceOpen(true)}
              onCancel={() => setEditing(null)}
              onSave={saveQuestion}
              onDelete={
                editing.isNew
                  ? undefined
                  : () => {
                      update({
                        questions: draft.questions.filter(
                          (q) => q.key !== editing.question.key
                        ),
                      });
                      setSelected((current) =>
                        current.filter((key) => key !== editing.question.key)
                      );
                      setEditing(null);
                    }
              }
            />
          )}
        </Offcanvas>
        <Modal
          isOpen={confirmCreation}
          toggle={() => setConfirmCreation(false)}
        >
          <ModalHeader toggle={() => setConfirmCreation(false)}>
            Confirm imported data
          </ModalHeader>
          <ModalBody>
            <p>
              Have you verified the imported checklist details and all{" "}
              {draft.questions.length} questions?
            </p>
            <p>
              Confirm that the imported data is correct before creating the
              checklist.
            </p>
          </ModalBody>
          <ModalFooter>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setConfirmCreation(false)}
            >
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={create}>
              I have verified the data, create checklist
            </button>
          </ModalFooter>
        </Modal>
        <Modal
          isOpen={sourceOpen}
          toggle={() => setSourceOpen(false)}
          size="lg"
        >
          <ModalHeader toggle={() => setSourceOpen(false)}>
            Source · {draft.sourceName || "Pasted text"}
          </ModalHeader>
          <ModalBody>
            <pre
              style={{
                whiteSpace: "pre-wrap",
                maxHeight: "60vh",
                overflow: "auto",
              }}
            >
              {draft.sourceText}
            </pre>
          </ModalBody>
        </Modal>
        <Modal isOpen={bulkOpen} toggle={() => setBulkOpen(false)}>
          <ModalHeader toggle={() => setBulkOpen(false)}>
            Bulk edit · {selected.length} questions
          </ModalHeader>
          <ModalBody>
            <div className="checklist-import">
              <label>
                Answer type
                <select
                  value={bulkType}
                  onChange={(e) => setBulkType(e.target.value)}
                >
                  {[
                    "None",
                    "Yes / No / N/A",
                    "Long text",
                    "Short text",
                    "Number",
                    "Date",
                  ].map((type) => (
                    <option key={type}>{type}</option>
                  ))}
                </select>
              </label>
              <button
                onClick={() => {
                  setDraft((current) => ({
                    ...current,
                    questions: current.questions.map((q) =>
                      selected.includes(q.key)
                        ? { ...q, answerType: bulkType, required: false }
                        : q
                    ),
                  }));
                  setBulkOpen(false);
                }}
              >
                Apply settings
              </button>{" "}
              <button
                onClick={() => {
                  setDraft((current) => ({
                    ...current,
                    questions: current.questions.map((q) =>
                      selected.includes(q.key) &&
                      q.question.trim() &&
                      q.referenceNo.trim() &&
                      q.section.trim()
                        ? { ...q, needsCheck: false }
                        : q
                    ),
                  }));
                  setBulkOpen(false);
                }}
              >
                Mark selected reviewed
              </button>
            </div>
          </ModalBody>
        </Modal>
      </div>
    </FullScreenPageLayout>
  );
}
